/**
 * SubBoardPreview Component - Thumbnail preview of the sub-board to execute per item
 *
 * Shows either:
 * - A placeholder with "Select Sub-Board" when no board is selected
 * - A simplified thumbnail preview when a board is selected
 *
 * The placeholder state provides a clear call-to-action for users to
 * choose which board/workflow to execute for each item in the loop.
 *
 * Note: This is a placeholder visualization. A full implementation would
 * capture actual board state as a thumbnail image, but that requires
 * additional infrastructure (canvas-to-image capture, storage, etc.).
 * For now, we show a simple stylized representation.
 */

// ============================================================================
// TYPES
// ============================================================================

interface SubBoardPreviewProps {
  /** ID of the selected sub-board (undefined if none selected) */
  boardId: string | undefined;
  /** Human-readable name of the board (optional, falls back to ID) */
  boardName?: string;
  /** Callback when user clicks to select/change the sub-board */
  onSelect?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * SubBoardPreview - Shows sub-board selection or preview
 *
 * Usage:
 * ```tsx
 * <SubBoardPreview
 *   boardId="board-123"
 *   boardName="Process Document"
 *   onSelect={() => openBoardSelector()}
 * />
 * ```
 */
export function SubBoardPreview({
  boardId,
  boardName,
  onSelect,
}: SubBoardPreviewProps) {
  // ==================== NO BOARD SELECTED ====================
  // Show placeholder with call-to-action button
  if (!boardId) {
    return (
      <button
        onClick={onSelect}
        className="
          w-full h-24 rounded-lg border-2 border-dashed border-gray-300
          flex flex-col items-center justify-center gap-2
          text-gray-400 hover:border-indigo-400 hover:text-indigo-500
          transition-colors cursor-pointer
        "
      >
        {/* Clipboard/document icon */}
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <span className="text-xs font-medium">Select Sub-Board</span>
      </button>
    );
  }

  // ==================== BOARD SELECTED ====================
  // Show simplified preview with change button
  return (
    <div className="relative group">
      {/* Preview container */}
      <div className="w-full h-24 rounded-lg border border-gray-200 bg-gray-50 p-3">
        {/* Mini board visualization - stylized placeholder */}
        <div className="w-full h-full bg-white rounded border border-gray-100 flex items-center justify-center">
          {/* Simplified node representation */}
          <div className="flex gap-1">
            {/* Three mini "nodes" representing a workflow */}
            <div className="w-4 h-3 rounded-sm bg-purple-200" />
            <div className="w-4 h-3 rounded-sm bg-indigo-200" />
            <div className="w-4 h-3 rounded-sm bg-blue-200" />
          </div>
        </div>
      </div>

      {/* Board name below preview */}
      <div className="mt-1 text-xs text-gray-600 truncate">
        {boardName || boardId}
      </div>

      {/* Change button - appears on hover */}
      <button
        onClick={onSelect}
        className="
          absolute top-2 right-2 px-2 py-0.5
          text-[10px] font-medium text-gray-600
          bg-white/90 rounded shadow-sm border border-gray-200
          opacity-0 group-hover:opacity-100 transition-opacity
          hover:bg-gray-50 cursor-pointer
        "
      >
        Change
      </button>
    </div>
  );
}
